import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, Lock, User, Phone, AlertCircle, Loader2 } from 'lucide-react';
import DomioLogo from '../components/common/DomioLogo';
import { useI18n } from '../i18n';
import {
  RegistrationPrivacyConsent,
  detectPrivacyJurisdiction,
} from '../components/auth/RegistrationPrivacyConsent';
import { isTenantUser } from '../lib/authRoles';
import { validateEmailAddress } from '../lib/emailValidation';
import { toast } from 'sonner';

const EMPTY_CONSENT = { privacy: false, sensitive: false, marketing: false };

function validateConsents(jurisdiction, consent, showSensitiveConsent) {
  const errors = {};
  if (!consent.privacy) errors.privacy = true;
  if (jurisdiction === 'CO' || jurisdiction === 'ES') {
    if (!consent.sensitive) errors.sensitive = true;
  }
  if (jurisdiction === 'AU' && showSensitiveConsent && !consent.sensitive) {
    errors.sensitive = true;
  }
  return errors;
}

export const RegisterPage = ({ locale: localeProp, countryCode: countryCodeProp }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    role: 'owner',
  });
  const [consent, setConsent] = useState(EMPTY_CONSENT);
  const [consentErrors, setConsentErrors] = useState({});
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const { register, error } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const jurisdiction = useMemo(
    () => detectPrivacyJurisdiction({ locale: localeProp, countryCode: countryCodeProp }),
    [localeProp, countryCodeProp],
  );

  /** AU sensitive checkbox only when financial/ID data is collected on this form (not at signup). */
  const showSensitiveConsent = false;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setValidationError('');
  };

  const handleRoleChange = (value) => {
    setFormData({ ...formData, role: value });
  };

  const handleConsentChange = (patch) => {
    setConsent((prev) => ({ ...prev, ...patch }));
    setConsentErrors((prev) => {
      const next = { ...prev };
      if (patch.privacy) delete next.privacy;
      if (patch.sensitive) delete next.sensitive;
      if (patch.marketing !== undefined) delete next.marketing;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailCheck = validateEmailAddress(formData.email);
    if (!emailCheck.valid) {
      setValidationError(
        emailCheck.reason === 'required' ? t('auth.emailRequired') : t('auth.invalidEmail'),
      );
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setValidationError(t('auth.passwordsDoNotMatch'));
      return;
    }

    if (formData.password.length < 6) {
      setValidationError(t('auth.passwordMinLength'));
      return;
    }

    const cErrors = validateConsents(jurisdiction, consent, showSensitiveConsent);
    if (Object.keys(cErrors).length > 0) {
      setConsentErrors(cErrors);
      return;
    }

    setIsLoading(true);
    try {
      const result = await register({
        name: formData.name,
        email: emailCheck.email,
        password: formData.password,
        phone: formData.phone || null,
        role: formData.role,
        privacyConsent: true,
        sensitiveDataConsent: consent.sensitive,
        marketingConsent: jurisdiction === 'ES' ? consent.marketing : false,
        privacyJurisdiction: jurisdiction,
      });

      if (result.success) {
        toast.success(t('auth.verificationEmailSent'));
        navigate(result.user && isTenantUser(result.user) ? '/app/tenant' : '/app/dashboard');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const displayError = validationError || error;

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage:
            'url(https://images.unsplash.com/photo-1664813953310-ea2953c0ec99?w=1200&q=80)',
        }}
      >
        <div className="absolute inset-0 bg-[#071E35]/70" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h2 className="font-manrope text-4xl font-bold text-white mb-4">
            {t('auth.startManaging')}
          </h2>
          <p className="text-slate-300 text-lg max-w-md">{t('auth.joinThousands')}</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 bg-slate-50 overflow-y-auto">
        <div className="w-full max-w-md py-6">
          <div className="flex items-center gap-2 mb-8">
            <DomioLogo size="xl" showText textClassName="text-slate-900" />
          </div>

          <h1 className="font-manrope text-3xl font-bold text-slate-900 mb-2">
            {t('auth.createAccount')}
          </h1>
          <p className="text-slate-500 mb-8">{t('auth.manageProperties')}</p>

          {displayError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-sm flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-domio-600 flex-shrink-0" />
              <p className="text-sm text-domio-700">{displayError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-700 font-medium">
                {t('auth.fullName')}
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('auth.enterFullName')}
                  className="pl-10 h-11 bg-white border-slate-200 focus:border-domio-600 focus:ring-domio-600/20"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">
                {t('auth.email')}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('auth.enterEmail')}
                  className="pl-10 h-11 bg-white border-slate-200 focus:border-domio-600 focus:ring-domio-600/20"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 font-medium">
                  {t('auth.password')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Password"
                    className="pl-10 h-11 bg-white border-slate-200 focus:border-domio-600 focus:ring-domio-600/20"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">
                  {t('auth.confirm')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm"
                    className="pl-10 h-11 bg-white border-slate-200 focus:border-domio-600 focus:ring-domio-600/20"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-700 font-medium">
                  {t('auth.phoneOptional')}
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Phone"
                    className="pl-10 h-11 bg-white border-slate-200 focus:border-domio-600 focus:ring-domio-600/20"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">{t('auth.role')}</Label>
                <Select value={formData.role} onValueChange={handleRoleChange}>
                  <SelectTrigger className="h-11 bg-white border-slate-200" data-testid="role-select">
                    <SelectValue placeholder={t('auth.selectRole')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Property Owner</SelectItem>
                    <SelectItem value="strata_manager">Strata Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <RegistrationPrivacyConsent
              locale={localeProp}
              countryCode={countryCodeProp}
              consent={consent}
              onConsentChange={handleConsentChange}
              errors={consentErrors}
              showSensitiveConsent={showSensitiveConsent}
              noticeOpen={noticeOpen}
              onNoticeOpenChange={setNoticeOpen}
            />

            <Button
              type="submit"
              className="w-full h-11 bg-domio-600 hover:bg-domio-700 text-white font-semibold rounded-sm"
              disabled={isLoading}
              data-testid="register-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('auth.creatingAccount')}
                </>
              ) : (
                t('auth.createAccount')
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-slate-500 text-sm max-w-md mx-auto">
            {t('auth.tenantInviteOnly') ||
              'Tenants: use the invitation link in your email to set your password — you cannot register here.'}
          </p>
          <p className="mt-6 text-center text-slate-500">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-domio-600 hover:text-domio-700 font-medium">
              {t('auth.login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
